/**
 * FieldProposalCard — TASK-928: a staged `field` proposal the RM can accept or dismiss.
 *
 * The review strip rendered `transition` proposals only. Everything staged as a `field` —
 * which after TASK-928 is every claim read out of an accepted document, and every life event
 * an email triage flags — had no card, no Accept button, and did not even count toward
 * opening the strip. Staging without a surface is worse than the direct write it replaced:
 * the work is recorded and then invisible.
 */

import React from 'react';
import { Check, X, FileText, Mail, Mic } from 'lucide-react';
import type { FieldPayload, StagedProposal } from '../../../services/kycProposalService';

interface FieldProposalCardProps {
  proposal: StagedProposal;
  onAccept: (proposalId: string) => void;
  onDismiss: (proposalId: string) => void;
  busy: boolean;
}

/** Where the claim came from, as an icon + words. Provenance is the point of the card. */
const SOURCE_LABEL: Record<string, { icon: React.ElementType; label: string }> = {
  document: { icon: FileText, label: 'From a document' },
  email: { icon: Mail, label: 'From an email' },
  meeting: { icon: Mic, label: 'From a meeting' },
  chat: { icon: FileText, label: 'Typed in chat' },
};

/** `personalInfo.place_of_birth` → `Place of birth`. */
const humanizeFieldPath = (path: string): string => {
  const leaf = path.split('.').slice(1).join('.') || path;
  const withoutIndex = leaf.replace(/\[[^\]]*\]$/, '');
  const spaced = withoutIndex.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const renderValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // A life-event proposal's value is an object; its `text` is the readable part.
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (typeof v.text === 'string') return v.text;
    return JSON.stringify(value);
  }
  return String(value);
};

const FieldProposalCard: React.FC<FieldProposalCardProps> = ({
  proposal,
  onAccept,
  onDismiss,
  busy,
}) => {
  // `payload` is a union (transition | field); this card only ever receives the field arm,
  // and the caller filters on `proposal_type === 'field'`. Narrowed rather than asserted
  // through, so a transition reaching here renders empty instead of throwing.
  const payload = (proposal.payload ?? {}) as Partial<FieldPayload>;
  const fieldPath = String(payload.field_path ?? '');
  const source = SOURCE_LABEL[proposal.source_kind] ?? SOURCE_LABEL.document;
  const SourceIcon = source.icon;
  const quote = proposal.source_ref?.quote;
  const confidence = proposal.confidence;

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
      data-testid="field-proposal-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-gray-500">
            {humanizeFieldPath(fieldPath)}
          </p>
          <p className="mt-0.5 break-words text-sm font-medium text-gray-900">
            {renderValue(payload.value)}
          </p>
        </div>
        {typeof confidence === 'number' && (
          <span
            className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
            title="How sure the reader was. It is not a reason to skip reading it."
          >
            {Math.round(confidence * 100)}%
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
        <SourceIcon className="h-3.5 w-3.5 shrink-0" />
        <span>{source.label}</span>
      </div>

      {quote && (
        <blockquote className="mt-2 border-l-2 border-gray-200 pl-2 text-xs italic text-gray-600">
          “{quote}”
        </blockquote>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={() => onAccept(proposal.id)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="field-proposal-accept"
        >
          <Check className="h-3.5 w-3.5" />
          Accept
        </button>
        <button
          onClick={() => onDismiss(proposal.id)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="field-proposal-dismiss"
        >
          <X className="h-3.5 w-3.5" />
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default FieldProposalCard;
